/**
 * Retirement Processing API services
 * Handles retirement cases, document checklists, pension workflows, and exit clearance
 */
import { api } from './api';
import type { ApiResponse } from '@/types';
import type { PaginatedApiResponse, PaginatedMeta } from './institution';

// Helper to convert snake_case to camelCase
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

// Transform object keys from snake_case to camelCase
function transformKeys<T>(obj: unknown): T {
  if (obj === null || obj === undefined) {
    return obj as T;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeys(item)) as T;
  }
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const camelKey = snakeToCamel(key);
      result[camelKey] = transformKeys(value);
    }
    return result as T;
  }
  return obj as T;
}

async function transformedGet<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<ApiResponse<T>> {
  const response = await api.get<unknown>(endpoint, params);
  if (response.success && response.data) {
    return {
      ...response,
      data: transformKeys<T>(response.data),
    };
  }
  return response as ApiResponse<T>;
}

async function transformedGetWithMeta<T>(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>
): Promise<PaginatedApiResponse<T>> {
  const response = await api.get<unknown>(endpoint, params);
  if (response.success && response.data) {
    const rawMeta = response.meta as PaginatedMeta | undefined;
    return {
      ...response,
      data: transformKeys<T>(response.data),
      meta: rawMeta,
    };
  }
  return response as PaginatedApiResponse<T>;
}

// ============================================
// Retirement Types
// ============================================

export type RetirementType = 'mandatory' | 'voluntary' | 'medical' | 'death_in_service' | 'compulsory';
export type RetirementStatus = 'pending' | 'documents_pending' | 'documents_verified' | 'pension_submitted' | 'pension_processing' | 'clearance_pending' | 'clearance_completed' | 'completed' | 'cancelled';
export type ChecklistItemStatus = 'pending' | 'submitted' | 'verified' | 'rejected' | 'waived';
export type ClearanceStatus = 'pending' | 'cleared' | 'blocked';
export type PensionStatus = 'not_submitted' | 'submitted' | 'processing' | 'approved' | 'rejected' | 'paid';

export interface RetirementCase {
  id: string;
  mdaId: string;
  caseNumber: string;
  staffId: string;
  pfileId: string | null;
  retirementType: RetirementType;
  expectedRetirementDate: string;
  actualRetirementDate: string | null;
  status: RetirementStatus;
  checklistCompleted: boolean;
  pensionStatus: PensionStatus;
  pensionReference: string | null;
  pensionSubmittedAt: string | null;
  clearanceCompleted: boolean;
  specialCircumstances: string | null;
  notes: string | null;
  initiatedBy: string;
  completedBy: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  staff: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    employeeId: string | null;
    jobTitle: string | null;
    department?: { id: string; name: string } | null;
  };
  pfile?: {
    id: string;
    fileNumber: string;
  } | null;
  initiator?: { firstName: string; lastName: string };
  completer?: { firstName: string; lastName: string } | null;
}

export interface RetirementChecklistItem {
  id: string;
  caseId: string;
  itemCode: string;
  itemName: string;
  description: string | null;
  isRequired: boolean;
  status: ChecklistItemStatus;
  documentId: string | null;
  submittedAt: string | null;
  submittedBy: string | null;
  verifiedBy: string | null;
  verifiedAt: string | null;
  verificationNotes: string | null;
  rejectionReason: string | null;
  waiverReason: string | null;
  waivedBy: string | null;
  waivedAt: string | null;
  createdAt: string;
  document?: {
    id: string;
    title: string;
    referenceNumber: string | null;
  } | null;
  submitter?: { firstName: string; lastName: string } | null;
  verifier?: { firstName: string; lastName: string } | null;
}

export interface ExitClearanceItem {
  id: string;
  caseId: string;
  departmentId: string;
  departmentName: string;
  status: ClearanceStatus;
  clearedBy: string | null;
  clearedAt: string | null;
  clearanceNotes: string | null;
  blockingReason: string | null;
  blockingItems: string | null;
  createdAt: string;
  clearer?: { firstName: string; lastName: string } | null;
}

export interface ApproachingRetirement {
  staffId: string;
  firstName: string;
  lastName: string;
  email: string;
  employeeId: string | null;
  jobTitle: string | null;
  departmentName: string | null;
  birthDate: string | null;
  hireDate: string | null;
  expectedRetirementDate: string;
  retirementType: 'mandatory' | 'voluntary';
  daysUntilRetirement: number;
  hasActiveCase: boolean;
  caseId: string | null;
}

export interface RetirementStats {
  total: number;
  pending: number;
  documentsPending: number;
  documentsVerified: number;
  pensionProcessing: number;
  clearancePending: number;
  completed: number;
  cancelled: number;
  byType: { type: RetirementType; count: number }[];
  approachingCount: number;
  overdueCount: number;
}

export interface RequiredDocument {
  code: string;
  name: string;
  description: string;
  isRequired: boolean;
  category: string;
}

export interface CreateRetirementCaseInput {
  staffId: string;
  retirementType: RetirementType;
  expectedRetirementDate: string;
  notes?: string;
  specialCircumstances?: string;
}

// ============================================
// Retirement API
// ============================================

export const retirementService = {
  // Approaching Retirements
  getApproaching: (months?: number) =>
    transformedGet<ApproachingRetirement[]>('/v1/retirement/approaching', { months }),

  // Retirement Cases
  listCases: (params?: {
    page?: number;
    perPage?: number;
    status?: string;
    retirementType?: string;
    search?: string;
  }) => transformedGetWithMeta<RetirementCase[]>('/v1/retirement/cases', params as Record<string, string | number | boolean | undefined>),

  getCaseById: (id: string) => transformedGet<RetirementCase>(`/v1/retirement/cases/${id}`),

  getStats: () => transformedGet<RetirementStats>('/v1/retirement/stats'),

  getRequiredDocuments: (retirementType: string) =>
    transformedGet<RequiredDocument[]>(`/v1/retirement/required-documents/${retirementType}`),

  createCase: async (input: CreateRetirementCaseInput): Promise<ApiResponse<RetirementCase>> => {
    const response = await api.post<unknown>('/v1/retirement/cases', input);
    if (response.success && response.data) {
      return { ...response, data: transformKeys<RetirementCase>(response.data) };
    }
    return response as ApiResponse<RetirementCase>;
  },

  // Document Checklist
  getChecklist: (caseId: string) =>
    transformedGet<RetirementChecklistItem[]>(`/v1/retirement/cases/${caseId}/checklist`),

  submitDocument: async (caseId: string, itemCode: string, documentId: string): Promise<ApiResponse<RetirementChecklistItem>> => {
    const response = await api.post<unknown>(`/v1/retirement/cases/${caseId}/checklist/${itemCode}/submit`, { documentId });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<RetirementChecklistItem>(response.data) };
    }
    return response as ApiResponse<RetirementChecklistItem>;
  },

  verifyDocument: async (caseId: string, itemCode: string, notes?: string): Promise<ApiResponse<RetirementChecklistItem>> => {
    const response = await api.post<unknown>(`/v1/retirement/cases/${caseId}/checklist/${itemCode}/verify`, { notes });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<RetirementChecklistItem>(response.data) };
    }
    return response as ApiResponse<RetirementChecklistItem>;
  },

  rejectDocument: async (caseId: string, itemCode: string, reason: string): Promise<ApiResponse<RetirementChecklistItem>> => {
    const response = await api.post<unknown>(`/v1/retirement/cases/${caseId}/checklist/${itemCode}/reject`, { reason });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<RetirementChecklistItem>(response.data) };
    }
    return response as ApiResponse<RetirementChecklistItem>;
  },

  waiveDocument: async (caseId: string, itemCode: string, reason: string): Promise<ApiResponse<RetirementChecklistItem>> => {
    const response = await api.post<unknown>(`/v1/retirement/cases/${caseId}/checklist/${itemCode}/waive`, { reason });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<RetirementChecklistItem>(response.data) };
    }
    return response as ApiResponse<RetirementChecklistItem>;
  },

  // Pension Processing
  submitToPension: async (caseId: string, pensionReference?: string): Promise<ApiResponse<RetirementCase>> => {
    const response = await api.post<unknown>(`/v1/retirement/cases/${caseId}/pension/submit`, { pensionReference });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<RetirementCase>(response.data) };
    }
    return response as ApiResponse<RetirementCase>;
  },

  updatePensionStatus: async (caseId: string, status: PensionStatus, notes?: string): Promise<ApiResponse<RetirementCase>> => {
    const response = await api.put<unknown>(`/v1/retirement/cases/${caseId}/pension/status`, { status, notes });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<RetirementCase>(response.data) };
    }
    return response as ApiResponse<RetirementCase>;
  },

  // Exit Clearance
  getClearance: (caseId: string) =>
    transformedGet<ExitClearanceItem[]>(`/v1/retirement/cases/${caseId}/clearance`),

  initializeClearance: async (caseId: string): Promise<ApiResponse<ExitClearanceItem[]>> => {
    const response = await api.post<unknown>(`/v1/retirement/cases/${caseId}/clearance/initialize`, {});
    if (response.success && response.data) {
      return { ...response, data: transformKeys<ExitClearanceItem[]>(response.data) };
    }
    return response as ApiResponse<ExitClearanceItem[]>;
  },

  recordClearance: async (caseId: string, itemId: string, notes?: string): Promise<ApiResponse<ExitClearanceItem>> => {
    const response = await api.post<unknown>(`/v1/retirement/cases/${caseId}/clearance/${itemId}/clear`, { notes });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<ExitClearanceItem>(response.data) };
    }
    return response as ApiResponse<ExitClearanceItem>;
  },

  blockClearance: async (caseId: string, itemId: string, reason: string, blockingItems?: string[]): Promise<ApiResponse<ExitClearanceItem>> => {
    const response = await api.post<unknown>(`/v1/retirement/cases/${caseId}/clearance/${itemId}/block`, { reason, blockingItems });
    if (response.success && response.data) {
      return { ...response, data: transformKeys<ExitClearanceItem>(response.data) };
    }
    return response as ApiResponse<ExitClearanceItem>;
  },

  // Complete Case
  completeCase: async (caseId: string): Promise<ApiResponse<RetirementCase>> => {
    const response = await api.post<unknown>(`/v1/retirement/cases/${caseId}/complete`, {});
    if (response.success && response.data) {
      return { ...response, data: transformKeys<RetirementCase>(response.data) };
    }
    return response as ApiResponse<RetirementCase>;
  },
};
